import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Building2,
  Globe,
  Mail,
  MapPin,
  Phone,
  Pencil,
  Coins,
  Clock3,
  CalendarRange,
  BadgeCheck,
  AlertCircle,
  AlarmClock,
  PartyPopper,
  Trash2,
  Plus,
} from 'lucide-react'
import { apiJson } from '../api'
import { hasAnyRole } from '../auth'

interface Organization {
  id: string
  name: string
  displayName?: string
  registrationNo?: string
  logoUrl?: string
  hqAddress?: string
  phone?: string
  email?: string
  website?: string
  timezone: string
  currency: string
  workDays: string[]
  workStartTime: string
  workEndTime: string
  lateGraceMinutes: number
  standardWorkMinutes: number
  createdAt: string
  updatedAt: string
}

interface Holiday {
  id: string
  date: string
  name: string
}

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
]

function InfoRow({ icon: Icon, label, value }: { icon: typeof Globe; label: string; value?: string }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="mt-0.5 rounded-lg bg-slate-100 p-2 text-slate-500">
        <Icon size={15} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
        <p className="mt-0.5 truncate text-sm font-medium text-slate-800">{value || '—'}</p>
      </div>
    </div>
  )
}

export default function Company() {
  const navigate = useNavigate()
  const canEdit = hasAnyRole('admin')
  const [org, setOrg] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  // holidays
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [holidayYear, setHolidayYear] = useState(String(new Date().getFullYear()))
  const [newHolidayDate, setNewHolidayDate] = useState('')
  const [newHolidayName, setNewHolidayName] = useState('')
  const [holidayError, setHolidayError] = useState('')
  const [holidayBusy, setHolidayBusy] = useState(false)

  const fetchOrg = useCallback(async () => {
    try {
      const data = await apiJson<Organization | null>('/organization')
      setOrg(data)
      setLoadError('')
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load company profile')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchOrg()
  }, [fetchOrg])

  const fetchHolidays = useCallback(async () => {
    try {
      setHolidays(await apiJson<Holiday[]>(`/holidays?year=${holidayYear}`))
      setHolidayError('')
    } catch {
      // holiday card degrades gracefully
    }
  }, [holidayYear])

  useEffect(() => {
    fetchHolidays()
  }, [fetchHolidays])

  const addHoliday = async () => {
    setHolidayBusy(true)
    setHolidayError('')
    try {
      await apiJson<Holiday>('/holidays', {
        method: 'POST',
        body: JSON.stringify({ date: newHolidayDate, name: newHolidayName }),
      })
      setNewHolidayDate('')
      setNewHolidayName('')
      await fetchHolidays()
    } catch (err) {
      setHolidayError(err instanceof Error ? err.message : 'Failed to add holiday')
    } finally {
      setHolidayBusy(false)
    }
  }

  const removeHoliday = async (id: string) => {
    setHolidayBusy(true)
    setHolidayError('')
    try {
      await apiJson(`/holidays/${id}`, { method: 'DELETE' })
      await fetchHolidays()
    } catch (err) {
      setHolidayError(err instanceof Error ? err.message : 'Failed to remove holiday')
    } finally {
      setHolidayBusy(false)
    }
  }

  if (loading) {
    return <div className="flex h-64 items-center justify-center text-sm text-slate-400">Loading company profile…</div>
  }
  if (loadError) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-600">
        <AlertCircle size={16} /> {loadError}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Hero card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 p-8 text-white shadow-lg">
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
        <div className="absolute -bottom-16 right-24 h-40 w-40 rounded-full bg-white/5" />
        <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-5">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-white/15 ring-1 ring-white/25">
              {org?.logoUrl ? (
                <img src={org.logoUrl} alt="logo" className="h-full w-full object-cover" />
              ) : (
                <Building2 size={34} className="text-white/85" />
              )}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/70">Organization</p>
              <h2 className="mt-1 text-2xl font-extrabold tracking-tight">
                {org?.displayName || org?.name || 'Not provisioned'}
              </h2>
              {org?.name && org.displayName && <p className="mt-0.5 text-sm text-white/70">{org.name}</p>}
              {org?.registrationNo && (
                <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/85">
                  <BadgeCheck size={12} /> Reg. {org.registrationNo}
                </p>
              )}
            </div>
          </div>
          {canEdit && (
            <button
              onClick={() => navigate('/company/edit')}
              className="inline-flex items-center gap-2 self-start rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-brand-700 shadow-sm transition-all hover:bg-brand-50 sm:self-center"
            >
              <Pencil size={15} /> {org ? 'Edit profile' : 'Provision organization'}
            </button>
          )}
        </div>
      </div>

      {!org ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <Building2 size={28} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-600">No organization provisioned yet</p>
          <p className="mt-1 text-xs text-slate-400">
            {canEdit ? 'Click "Provision organization" to create the company profile.' : 'Ask an administrator to provision the company profile.'}
          </p>
        </div>
      ) : (
        <>
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Identity & contact */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-sm font-bold tracking-tight">Identity & Contact</h3>
            <div className="mt-3 divide-y divide-slate-50">
              <InfoRow icon={MapPin} label="Headquarters" value={org.hqAddress} />
              <InfoRow icon={Phone} label="Phone" value={org.phone} />
              <InfoRow icon={Mail} label="Email" value={org.email} />
              <InfoRow icon={Globe} label="Website" value={org.website} />
            </div>
          </div>

          {/* Work settings */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-sm font-bold tracking-tight">Work Settings</h3>
            <div className="mt-3 divide-y divide-slate-50">
              <InfoRow icon={Clock3} label="Timezone" value={org.timezone} />
              <InfoRow icon={Coins} label="Currency" value={org.currency} />
              <InfoRow
                icon={AlarmClock}
                label="Work hours"
                value={`${org.workStartTime} – ${org.workEndTime} · grace ${org.lateGraceMinutes}m · standard ${Math.floor(org.standardWorkMinutes / 60)}h${org.standardWorkMinutes % 60 ? ` ${org.standardWorkMinutes % 60}m` : ''}`}
              />
              <div className="flex items-start gap-3 py-2.5">
                <div className="mt-0.5 rounded-lg bg-slate-100 p-2 text-slate-500">
                  <CalendarRange size={15} />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Work week</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {DAYS.map((d) => (
                      <span
                        key={d.key}
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${
                          org.workDays.includes(d.key)
                            ? 'bg-brand-50 text-brand-700 ring-brand-100'
                            : 'bg-slate-50 text-slate-300 ring-slate-100'
                        }`}
                      >
                        {d.label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Public holidays */}
      {org && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="flex items-center gap-2 text-sm font-bold tracking-tight">
              <PartyPopper size={15} className="text-brand-600" /> Public Holidays
            </h3>
            <select
              value={holidayYear}
              onChange={(e) => setHolidayYear(e.target.value)}
              className="w-fit rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            >
              {[0, 1, 2].map((off) => {
                const y = String(new Date().getFullYear() - 1 + off)
                return <option key={y} value={y}>{y}</option>
              })}
            </select>
          </div>

          {holidayError && (
            <p className="mt-3 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-2.5 text-xs font-medium text-red-600">
              <AlertCircle size={14} /> {holidayError}
            </p>
          )}

          {canEdit && (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                type="date"
                value={newHolidayDate}
                onChange={(e) => setNewHolidayDate(e.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
              />
              <input
                type="text"
                value={newHolidayName}
                onChange={(e) => setNewHolidayName(e.target.value)}
                placeholder="Holiday name (e.g. Independence Day)"
                className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
              />
              <button
                onClick={addHoliday}
                disabled={holidayBusy || !newHolidayDate || !newHolidayName.trim()}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-brand-700 disabled:opacity-50"
              >
                <Plus size={14} /> Add
              </button>
            </div>
          )}

          <div className="mt-4 divide-y divide-slate-50">
            {holidays.map((h) => (
              <div key={h.id} className="flex items-center justify-between py-2.5">
                <div className="flex items-center gap-3">
                  <span className="rounded-lg bg-sky-50 px-2.5 py-1 text-xs font-bold tabular-nums text-sky-700 ring-1 ring-sky-100">
                    {h.date.slice(0, 10)}
                  </span>
                  <span className="text-sm font-medium text-slate-800">{h.name}</span>
                </div>
                {canEdit && (
                  <button
                    onClick={() => removeHoliday(h.id)}
                    disabled={holidayBusy}
                    className="rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                    title="Remove holiday"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            {holidays.length === 0 && (
              <p className="py-6 text-center text-xs text-slate-400">No holidays recorded for {holidayYear}.</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
